import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '@htx/environment';
import {
  UseCaseParticipationRequest,
  UseCaseParticipationComment
} from './use-case-participation.model';
import {
  PageQueryParams,
  PaginatedList,
  SpringPageApiDto
} from '../organizations/organization.model';
import { HttpUtilsService } from '../http-utils.service';

@Injectable({
  providedIn: 'root'
})
export class UseCaseParticipationService {

  private httpClient = inject(HttpClient);
  private httpUtils = inject(HttpUtilsService);
  private readonly baseUrl = `${environment.backendUrl}/use-cases`;

  getMyStatus(useCaseSlug: string): Observable<UseCaseParticipationRequest | null> {
    return this.httpClient.get<UseCaseParticipationRequest>(
      `${this.baseUrl}/${useCaseSlug}/participation/status`,
      { observe: 'response' }
    ).pipe(
      map((response: HttpResponse<UseCaseParticipationRequest>) =>
        response.status === 204 ? null : response.body
      )
    );
  }

  requestParticipation(useCaseId: string): Observable<UseCaseParticipationRequest> {
    return this.httpClient.post<UseCaseParticipationRequest>(
      `${this.baseUrl}/${useCaseId}/participation`, {}
    );
  }

  findAll(): Observable<UseCaseParticipationRequest[]> {
    return this.httpClient.get<UseCaseParticipationRequest[]>(
      `${this.baseUrl}/participation`
    );
  }

  getDetailsById(id: string): Observable<UseCaseParticipationRequest> {
    return this.httpClient.get<UseCaseParticipationRequest>(
      `${this.baseUrl}/participation/${id}`
    );
  }

  approve(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.baseUrl}/participation/${id}/approve`,
      { reasoning }
    );
  }

  reject(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.baseUrl}/participation/${id}/reject`,
      { reasoning }
    );
  }

  getComments(
    id: string,
    queryParams: PageQueryParams = { page: 0, size: 5, sort: 'date,desc' }
  ): Observable<PaginatedList<UseCaseParticipationComment>> {
    const params = this.httpUtils.toHttpParams(queryParams);
    return this.httpClient
      .get<SpringPageApiDto<UseCaseParticipationComment>>(
        `${this.baseUrl}/participation/${id}/comments`,
        { params }
      )
      .pipe(
        map((response) => ({
          content: response.content,
          pagination: {
            pageNumber: response.pageable.pageNumber,
            pageSize: response.pageable.pageSize,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            last: response.last,
            first: response.first,
            numberOfElements: response.numberOfElements,
            size: response.size,
            number: response.number,
            empty: response.empty
          },
          sort: response.sort
        }))
      );
  }

  addComment(id: string, comment: { text: string }): Observable<UseCaseParticipationComment> {
    return this.httpClient.post<UseCaseParticipationComment>(
      `${this.baseUrl}/participation/${id}/comments`,
      comment
    );
  }
}
